/**
 * ChatListPage Component
 *
 * Main page component for the Chat list view. Displays a list of chat channels with filtering
 * and search capabilities.
 *
 * ## Interactive Support Integration
 *
 * This component integrates the Interactive Support feature. When a user clicks
 * the "Support" pill in the filter tabs:
 *
 * 1. **Checks Interactive Support flag**: `useIsInteractiveSupportEnabled()` - determines if
 *    the new guided support flow is enabled for this user
 * 2. **If Interactive Support enabled**: Navigates directly to `/home-v2/support/guided`
 *    where users can select a shift and report issues through a guided flow
 * 3. **If Interactive Support disabled**: Falls back to legacy behavior:
 *    a. Checks timeclock info flag and shift status
 *    b. If both conditions met: Shows `GetHelpDuringShiftBottomSheet`
 *    c. Otherwise: Opens Zendesk messaging directly
 *
 * This provides a unified support experience where users can select a shift before
 * contacting support, matching the same flow as the "Contact Support" button on the Account page.
 *
 * ## Feature Flags
 *
 * - `INTERACTIVE_SUPPORT_ENABLED` (2026-01-interactive-support-enabled): Primary flag for new experience
 * - `TIMECLOCK_INFO_WHEN_CONTACTING_SUPPORT`: Legacy flag for mid-shift intervention (fallback)
 *
 * ## User Flow (Interactive Support Enabled)
 *
 * 1. User clicks "Support" pill in Chat filter tabs
 * 2. System navigates to `/home-v2/support/guided`
 * 3. User sees list of recent/upcoming shifts to select from
 * 4. User selects shift and views details with "Report an Issue" button
 * 5. Clicking "Report an Issue" opens Zendesk with shift context
 *
 * ## Entry Points
 *
 * Both entry points now lead to the same Interactive Support experience:
 * - **Account page**: "Contact Support" button → `/home-v2/support/guided`
 * - **Chat page**: "Support" pill → `/home-v2/support/guided`
 */

import { useModalState } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";

import { PullToRefresh } from "../../lib/PullToRefresh/PullToRefresh";
import { useDebounce } from "../../lib/utils/useDebounce";
import { openZendeskMessaging } from "../../lib/ZendeskMessaging/openZendeskMessaging";
import { GetHelpDuringShiftBottomSheet } from "../../redesign/Accounts/components/GetHelpDuringShiftBottomSheet";
import { useHasUpcomingOrInProgressShift } from "../../redesign/Accounts/hooks/useHasUpcomingOrInProgressShift";
import { useIsTimeclockInfoWhenContactingSupportEnabled } from "../../redesign/Accounts/hooks/useIsTimeclockInfoWhenContactingSupportEnabled";
import { useIsInteractiveSupportEnabled } from "../../redesign/Support/hooks/useIsInteractiveSupportEnabled";
import { GUIDED_SUPPORT_FULL_PATH } from "../../redesign/Support/paths";
import { type ChatChannel, type ChatChannelCustomTypesFilter } from "../types";
import { useDeprecatedChatChannelStore } from "../useDeprecatedChatChannelStore";
import { ChatListContent } from "./Content";
import { ChatListHeader } from "./Header";
import { useFilterChannelsByWorkplaceStatus } from "./hooks/useFilterChannelsByWorkplaceStatus";
import { useGetChatChannels } from "./hooks/useGetChatChannels";

export function LegacyChatListPage() {
  const [filterType, setFilterType] = useState<ChatChannelCustomTypesFilter>("all");
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce({ value: searchValue, debounceTimeInMs: 300 });
  const { channels, setChannels } = useDeprecatedChatChannelStore();
  const history = useHistory();

  // Feature flag check: determines if Interactive Support is enabled for this user
  // When enabled, navigates to guided support page instead of opening Zendesk directly
  const isInteractiveSupportEnabled = useIsInteractiveSupportEnabled();

  // Legacy feature flag check: determines if mid-shift intervention is enabled
  // This is the fallback behavior when Interactive Support is disabled
  const isTimeclockInfoEnabled = useIsTimeclockInfoWhenContactingSupportEnabled();

  // Shift status check: determines if worker has an in-progress shift
  // A shift is in-progress if: start time < now < end time
  // This is required for the legacy intervention to appear
  const hasUpcomingOrInProgressShift = useHasUpcomingOrInProgressShift();

  // Modal state for the mid-shift intervention bottom sheet
  // Controls the visibility of GetHelpDuringShiftBottomSheet component
  const getHelpDuringShiftModalState = useModalState();

  const { refetch, isFetching: isFetchingChannels } = useGetChatChannels({
    searchInput: debouncedSearch,
    customTypesFilter: filterType,
    onSuccess: (data) => {
      void setChannels(data.channels);
    },
    enabled: filterType !== "support",
  });

  // Filter out channels for workplaces with inactive statuses (SUSPENDED, TERMINATED, ARCHIVED)
  const { filteredChannels, isLoading: isLoadingWorkplaceStatuses } =
    useFilterChannelsByWorkplaceStatus({
      channels: channels as unknown as ChatChannel[],
      enabled: filterType !== "support",
    });

  const refetchChannels = useCallback(async () => {
    if (filterType === "support") {
      await setChannels([]);
      return;
    }

    await refetch();
  }, [filterType, refetch, setChannels]);

  /**
   * Handles Support pill click from the filter tabs.
   *
   * Decision logic (in priority order):
   * 1. If Interactive Support is enabled:
   *    → Navigate to Guided Support page (`/home-v2/support/guided`)
   *    → User can select a shift and view details before contacting support
   * 2. If legacy timeclock info flag is enabled AND worker has an in-progress shift:
   *    → Show mid-shift intervention bottom sheet (GetHelpDuringShiftBottomSheet)
   * 3. Otherwise:
   *    → Open Zendesk messaging directly (default support flow)
   *
   * This ensures a unified support experience across all entry points:
   * - "Contact Support" button on Account page
   * - "Support" pill on Chat page
   *
   * @see GUIDED_SUPPORT_FULL_PATH for the navigation destination
   * @see GetHelpDuringShiftBottomSheet for details on the legacy intervention flow
   */
  const handleSupportClick = useCallback(async () => {
    // Priority 1: New Interactive Support experience
    if (isInteractiveSupportEnabled) {
      history.push(GUIDED_SUPPORT_FULL_PATH);
      return;
    }

    // Priority 2: Legacy mid-shift intervention (fallback when Interactive Support disabled)
    if (isTimeclockInfoEnabled && hasUpcomingOrInProgressShift) {
      getHelpDuringShiftModalState.openModal();
      return;
    }

    // Priority 3: Default behavior - open Zendesk messaging directly
    await openZendeskMessaging();
  }, [
    isInteractiveSupportEnabled,
    isTimeclockInfoEnabled,
    hasUpcomingOrInProgressShift,
    getHelpDuringShiftModalState,
    history,
  ]);

  return (
    <Stack height="100%">
      <ChatListHeader
        searchValue={searchValue}
        filterType={filterType}
        onSearchChange={setSearchValue}
        onFilterTypeChange={setFilterType}
        onSupportClick={handleSupportClick}
      />

      <Box sx={{ flex: 1, overflow: "auto" }}>
        <PullToRefresh onRefresh={refetchChannels} />

        <Box sx={{ padding: 5, paddingTop: 3 }}>
          <ChatListContent
            channels={filteredChannels}
            isFetchingChannels={isFetchingChannels || isLoadingWorkplaceStatuses}
            filterType={filterType}
            onFilterTypeChange={setFilterType}
          />
        </Box>
      </Box>

      {/* Mid-shift intervention bottom sheet - same experience as from Account page */}
      <GetHelpDuringShiftBottomSheet modalState={getHelpDuringShiftModalState} />
    </Stack>
  );
}
